/**
 * Agrupamento da preventiva por PLACA — a placa aparece uma vez, com seus itens.
 * Só apresentação: o status de cada item vem pronto do servidor.
 */
import type { MaintenancePlanRowDto, MaintenancePlanStatusDto } from "@bi/types";

export interface PlateGroup {
  plate: string;
  isTrailer: boolean;
  vehicleModel: string | null;
  group: MaintenancePlanRowDto["group"];
  currentKm: MaintenancePlanRowDto["currentKm"];
  kmStale: boolean;
  /** pior situação entre os itens (a ordem de urgência da tela) */
  worst: MaintenancePlanStatusDto;
  rows: MaintenancePlanRowDto[];
}

const URGENCIA: MaintenancePlanStatusDto[] = ["vencido", "vencendo", "km_inconsistente", "sem_km", "sem_base", "em_dia"];

export function groupByPlate(rows: MaintenancePlanRowDto[]): PlateGroup[] {
  const mapa = new Map<string, MaintenancePlanRowDto[]>();
  for (const r of rows) mapa.set(r.plate, [...(mapa.get(r.plate) ?? []), r]);
  const grupos: PlateGroup[] = [...mapa.entries()].map(([plate, itens]) => {
    const primeiro = itens[0]!;
    const worst = URGENCIA.find((s) => itens.some((i) => i.status.status === s)) ?? "em_dia";
    return {
      plate,
      isTrailer: primeiro.isTrailer,
      vehicleModel: primeiro.vehicleModel ?? null,
      group: primeiro.group ?? null,
      currentKm: primeiro.currentKm,
      kmStale: itens.some((i) => i.status.kmStale),
      worst,
      rows: itens,
    };
  });
  return grupos.sort((a, b) => URGENCIA.indexOf(a.worst) - URGENCIA.indexOf(b.worst) || a.plate.localeCompare(b.plate));
}

export function plateSummary(rows: MaintenancePlanRowDto[]) {
  const n = (...s: MaintenancePlanStatusDto[]) => rows.filter((r) => s.includes(r.status.status)).length;
  return { vencido: n("vencido"), proximo: n("vencendo"), emDia: n("em_dia"), semBase: n("sem_base", "sem_km"), outros: n("km_inconsistente") };
}

/**
 * Itens que entram por padrão em "Abrir O.S.": TODOS os elegíveis, ou seja, os
 * que ainda não estão em outra O.S. e cujo KM não está inconsistente. Vêm todos
 * selecionados; quem revisa a O.S. desmarca o que não for fazer. A ordem é a da
 * urgência (vencidos primeiro).
 */
export function suggestedForOs(rows: MaintenancePlanRowDto[]): MaintenancePlanRowDto[] {
  return rows.filter((r) => !r.openWorkOrder && r.status.status !== "km_inconsistente");
}

/**
 * Ícone do item preventivo — recortes da referência (public/manutencao-bi).
 * `lista` = o par usado na consulta por placa; `cartao` = o da lista por placa.
 */
export function preventiveIconSrc(label: string, variante: "lista" | "cartao"): string | null {
  const t = label.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const p = variante === "lista" ? "i" : "r";
  const arq = (a: string, b: string) => `/manutencao-bi/${variante === "lista" ? a : b}.png`;
  if (/combustivel/.test(t)) return `/manutencao-bi/r-comb.png`;
  if (/filtro/.test(t)) return arq("i-filtro", "r-filtro");
  if (/oleo/.test(t)) return arq("i-oleo", "r-oleo");
  if (/freio/.test(t)) return arq("i-freio", "r-freio");
  if (/graxa|lubrific/.test(t)) return variante === "lista" ? `/manutencao-bi/i-graxa.png` : `/manutencao-bi/r-oleo.png`;
  if (/pneu|rodizio|calibrag/.test(t)) return `/manutencao-bi/r-pneu.png`;
  if (/ar-?\s?condicionado|higieniz/.test(t)) return `/manutencao-bi/r-ar.png`;
  void p;
  return null;
}
